import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { NUM_INITIAL_BUDGETING_GAME_QUESTIONS } from "src/constants";

import {
  useAddBudgetPlanOption,
  useBudgetCategories,
  useBudgetCategoryOptions,
  useBudgetPlan,
  useCreateBudgetPlan,
  useDeleteBudgetPlan,
  useUpdateBudgetPlan,
} from "../api";
import { EMERGENCY_BUDGET_CODE } from "../constants";
import {
  BUDGET_GAME_STATES,
  PLAN_CREATION_FAILURE_REASONS,
  parsePlanCreationError,
  useBudgetGameState,
} from "./use-budget-game-state";
import { getStateEffect } from "./use-budget-game-state/effects";

const PLAN_CREATION_FAILURE_MESSAGES = {
  [PLAN_CREATION_FAILURE_REASONS.lightcastNoData]:
    "We couldn't find salary data for this career and region. Try a different region or career.",
  [PLAN_CREATION_FAILURE_REASONS.invalidInput]:
    "The salary provided isn't valid. Please try again.",
  [PLAN_CREATION_FAILURE_REASONS.unknown]:
    "Couldn't start the budget. Please try again.",
};

/**
 * Action types for the budget game controller.
 * All state-changing operations flow through dispatch() for consistency.
 */
export const BUDGET_GAME_ACTIONS = {
  // User-initiated actions
  SELECT_OPTION: "SELECT_OPTION",
  CALCULATE_TAKE_HOME_PAY: "CALCULATE_TAKE_HOME_PAY",
  CHANGE_TAX_RATE: "CHANGE_TAX_RATE",
  START_OVER: "START_OVER",
  RETRY_PLAN_CREATION: "RETRY_PLAN_CREATION",
  NAVIGATE_NEXT: "NAVIGATE_NEXT",
  NAVIGATE_PREVIOUS: "NAVIGATE_PREVIOUS",
  NAVIGATE_TO_SLIDE: "NAVIGATE_TO_SLIDE",
  EDIT_CATEGORY: "EDIT_CATEGORY",
  VIEW_SUMMARY: "VIEW_SUMMARY",
  COMPLETE_BUDGET: "COMPLETE_BUDGET",

  // Internal actions (triggered by effects/automation)
  CREATE_BUDGET_PLAN: "CREATE_BUDGET_PLAN",
  UPDATE_BUDGET_CATEGORY: "UPDATE_BUDGET_CATEGORY",
};

/**
 * Controller hook that combines state machine, API mutations, and effect execution.
 * This is the single source of truth for the budgeting game.
 *
 * @param {Object} params
 * @param {Array} params.slideConfigs - Configuration for budget slides
 * @param {string} params.transfrId - The career transfr ID
 * @param {Function} params.onBudgetComplete - Callback when budget is completed
 */
export const useBudgetGameController = ({
  slideConfigs,
  transfrId,
  onBudgetComplete,
}) => {
  // Track previous state for effect execution
  const prevStateRef = useRef(null);

  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState();
  const clearError = useCallback(() => setErrorMessage(), []);

  // Tracks emergency auto-selection per category code so rapid re-renders
  // don't trigger a double POST with a different random option. Cleared only
  // on START_OVER.
  const autoSelectedCategoriesRef = useRef(new Set());

  // ===== DATA QUERIES =====

  const {
    data: budgetPlan,
    isFetched: isBudgetPlanFetched,
    isFetching: isBudgetPlanFetching,
  } = useBudgetPlan();

  const { data: budgetCategories, isFetched: isBudgetCategoriesFetched } =
    useBudgetCategories();

  // ===== STATE MACHINE =====

  const stateMachine = useBudgetGameState(slideConfigs);
  const {
    state: gameState,
    context: gameContext,
    budgetPlanLoaded,
    budgetCategoriesLoaded,
    calculatorCompleted,
    optionAdded,
    planCreationFailed,
    regionalTaxRateChanged,
    startOver: stateMachineStartOver,
    goToNextSlide,
    goToPreviousSlide,
    goToSlide,
    currentSlideConfig,
    budgetPlanOptionsByBudgetCategoryCode,
    canAdvance,
    canViewSummary,
    isFirstSlide,
    isLastSlide,
  } = stateMachine;

  // ===== API MUTATIONS =====

  const { mutateAsync: createBudgetPlan, isPending: isCreatingBudgetPlan } =
    useCreateBudgetPlan();

  const {
    mutateAsync: updateBudgetPlanAsync,
    isPending: isUpdatingBudgetPlan,
  } = useUpdateBudgetPlan(budgetPlan?.id);

  const { mutateAsync: deleteBudgetPlan, isPending: isDeletingBudgetPlan } =
    useDeleteBudgetPlan();

  const {
    mutateAsync: addBudgetPlanOptionAsync,
    isPending: isAddingBudgetPlanOption,
    isSuccess: isAddBudgetPlanOptionSuccess,
  } = useAddBudgetPlanOption(budgetPlan?.id);

  // ===== DATA LOADING EFFECTS =====

  // Sync budget plan data with state machine when fetched, or when context is
  // reset (e.g. after RETRY_PLAN_CREATION) while cached data is still available.
  // Skip while a refetch is in-flight: after START_OVER, the cache still holds
  // the old completed plan (stale-while-revalidate) until the post-delete
  // refetch lands. Re-syncing that stale plan would drive the machine back
  // into `complete`, pinning the carousel on the Summary slide.
  useEffect(() => {
    if (isBudgetPlanFetched && budgetPlan && !isBudgetPlanFetching) {
      budgetPlanLoaded(budgetPlan);
    }
  }, [
    budgetPlan,
    isBudgetPlanFetched,
    isBudgetPlanFetching,
    budgetPlanLoaded,
    // Trigger-only dep: startOver resets this to false without changing
    // budgetPlan, so the effect re-fires and re-syncs the cached plan.
    gameContext.budgetPlanLoaded,
  ]);

  // Sync budget categories with state machine when fetched
  useEffect(() => {
    if (isBudgetCategoriesFetched && Object.keys(budgetCategories).length > 0) {
      budgetCategoriesLoaded(budgetCategories);
    }
  }, [budgetCategories, isBudgetCategoriesFetched, budgetCategoriesLoaded]);

  // Sync budget plan options with state machine when they change
  useEffect(() => {
    if (budgetPlan?.id && budgetPlan?.options) {
      optionAdded(budgetPlan);
    }
  }, [budgetPlan?.id, budgetPlan?.options, optionAdded]);

  // ===== ACTION DISPATCHER =====

  const dispatch = useCallback(
    async (action) => {
      switch (action.type) {
        case BUDGET_GAME_ACTIONS.SELECT_OPTION: {
          if (!budgetPlan?.id) {
            console.warn("Cannot select option without budget plan");
            return;
          }
          try {
            return await addBudgetPlanOptionAsync(action.payload.optionId);
          } catch (error) {
            console.error("SELECT_OPTION failed", error);
            setErrorMessage("Couldn't save your selection. Please try again.");
            throw error;
          }
        }

        case BUDGET_GAME_ACTIONS.CALCULATE_TAKE_HOME_PAY: {
          calculatorCompleted();
          return;
        }

        case BUDGET_GAME_ACTIONS.CHANGE_TAX_RATE: {
          const { taxRate } = action.payload;
          if (!budgetPlan?.id) {
            regionalTaxRateChanged(taxRate);
            return;
          }
          try {
            await updateBudgetPlanAsync({ tax_rate_id: taxRate.id });
            regionalTaxRateChanged(taxRate);
          } catch (error) {
            console.error("CHANGE_TAX_RATE failed", error);
            setErrorMessage("Couldn't update region. Please try again.");
            throw error;
          }
          return;
        }

        case BUDGET_GAME_ACTIONS.START_OVER: {
          autoSelectedCategoriesRef.current = new Set();
          await deleteBudgetPlan();
          stateMachineStartOver();
          return;
        }

        case BUDGET_GAME_ACTIONS.NAVIGATE_NEXT: {
          goToNextSlide();
          return;
        }

        case BUDGET_GAME_ACTIONS.NAVIGATE_PREVIOUS: {
          goToPreviousSlide();
          return;
        }

        case BUDGET_GAME_ACTIONS.NAVIGATE_TO_SLIDE: {
          goToSlide(action.payload.slideIndex);
          return;
        }

        case BUDGET_GAME_ACTIONS.EDIT_CATEGORY: {
          const { categoryCode, slideIndex } = action.payload;
          if (budgetPlan?.id) {
            try {
              await updateBudgetPlanAsync({
                budget_category_code: categoryCode,
              });
            } catch (error) {
              console.error("EDIT_CATEGORY failed", error);
              setErrorMessage("Couldn't edit category. Please try again.");
              throw error;
            }
          }
          goToSlide(slideIndex);
          return;
        }

        case BUDGET_GAME_ACTIONS.VIEW_SUMMARY: {
          const summarySlideIndex =
            slideConfigs.length + NUM_INITIAL_BUDGETING_GAME_QUESTIONS;
          goToSlide(summarySlideIndex);
          return;
        }

        case BUDGET_GAME_ACTIONS.COMPLETE_BUDGET: {
          if (budgetPlan?.id) {
            try {
              await updateBudgetPlanAsync({ budget_completed: true });
            } catch (error) {
              console.error("COMPLETE_BUDGET failed", error);
              setErrorMessage(
                "Couldn't complete your budget. Please try again.",
              );
              throw error;
            }
          }
          await onBudgetComplete?.();
          return;
        }

        case BUDGET_GAME_ACTIONS.CREATE_BUDGET_PLAN: {
          const { transfrId: planTransfrId } = action.payload;
          try {
            return await createBudgetPlan({ transfr_id: planTransfrId });
          } catch (error) {
            const parsed = parsePlanCreationError(error);
            console.error("CREATE_BUDGET_PLAN failed", {
              ...parsed,
              error,
            });
            planCreationFailed(parsed);
            setErrorMessage(
              PLAN_CREATION_FAILURE_MESSAGES[parsed.reason] ??
                PLAN_CREATION_FAILURE_MESSAGES[
                  PLAN_CREATION_FAILURE_REASONS.unknown
                ],
            );
            // Don't re-throw: the useEffect caller doesn't await this dispatch,
            // so planCreationFailed() above is the only way to surface failure.
            return;
          }
        }

        case BUDGET_GAME_ACTIONS.RETRY_PLAN_CREATION: {
          setErrorMessage();
          autoSelectedCategoriesRef.current = new Set();
          stateMachineStartOver();
          return;
        }

        case BUDGET_GAME_ACTIONS.UPDATE_BUDGET_CATEGORY: {
          const { categoryCode } = action.payload;
          if (!budgetPlan?.id) {
            return;
          }
          try {
            return await updateBudgetPlanAsync({
              budget_category_code: categoryCode,
            });
          } catch (error) {
            console.error("UPDATE_BUDGET_CATEGORY failed", error);
            setErrorMessage("Couldn't update category. Please try again.");
            throw error;
          }
        }

        default: {
          console.warn(`Unknown action type: ${action.type}`);
          return;
        }
      }
    },
    [
      budgetPlan?.id,
      addBudgetPlanOptionAsync,
      calculatorCompleted,
      regionalTaxRateChanged,
      updateBudgetPlanAsync,
      deleteBudgetPlan,
      createBudgetPlan,
      planCreationFailed,
      stateMachineStartOver,
      goToNextSlide,
      goToPreviousSlide,
      goToSlide,
      slideConfigs.length,
      onBudgetComplete,
    ],
  );

  // ===== STATE-DRIVEN EFFECTS =====

  // Track previous values for effect execution
  const prevBudgetCategoryCodeRef = useRef(null);

  // Execute effects when state changes or when slide changes within budgeting state
  useEffect(() => {
    const currentStateName = gameState.name;
    const prevStateName = prevStateRef.current;
    const currentBudgetCategoryCode = currentSlideConfig?.budgetCategoryCode;
    const prevBudgetCategoryCode = prevBudgetCategoryCodeRef.current;

    const stateChanged = currentStateName !== prevStateName;
    const slideChangedInBudgeting =
      currentStateName === BUDGET_GAME_STATES.budgeting &&
      currentBudgetCategoryCode !== prevBudgetCategoryCode &&
      currentBudgetCategoryCode !== undefined;

    // Skip if neither state nor slide changed
    if (!stateChanged && !slideChangedInBudgeting) {
      return;
    }

    // Update refs
    if (stateChanged) {
      prevStateRef.current = currentStateName;
    }
    // Update budget category ref when slide changes
    if (slideChangedInBudgeting) {
      prevBudgetCategoryCodeRef.current = currentBudgetCategoryCode;
    }

    const effect = getStateEffect(currentStateName, "onEnter");
    if (!effect) {
      return;
    }

    const effectContext = {
      dispatch,
      transfrId,
      budgetPlanId: budgetPlan?.id,
      currentBudgetCategoryCode,
    };

    effect(effectContext);
  }, [
    gameState.name,
    dispatch,
    transfrId,
    budgetPlan?.id,
    currentSlideConfig?.budgetCategoryCode,
  ]);

  useEffect(() => {
    if (isAddingBudgetPlanOption || isDeletingBudgetPlan) {
      setIsSaving(true);
    } else if (!isBudgetPlanFetching) {
      setIsSaving(false);
    }
  }, [isAddingBudgetPlanOption, isDeletingBudgetPlan, isBudgetPlanFetching]);

  // ===== AUTO-SELECTION LOGIC =====

  // Check if current category has single option and auto-select
  const currentCategoryId = useMemo(() => {
    if (!currentSlideConfig?.budgetCategoryCode || !budgetCategories) {
      return;
    }
    return budgetCategories[currentSlideConfig.budgetCategoryCode]?.id;
  }, [currentSlideConfig?.budgetCategoryCode, budgetCategories]);

  const { data: currentCategoryOptions } =
    useBudgetCategoryOptions(currentCategoryId);

  // Auto-select single option when entering a category
  useEffect(() => {
    const categoryCode = currentSlideConfig?.budgetCategoryCode;
    if (
      // No budget plan
      !budgetPlan?.id ||
      // Waiting for budget plan option to be added
      isAddingBudgetPlanOption ||
      // Waiting for updated budget plan to be fetched
      isBudgetPlanFetching ||
      // Not in the budgeting state
      gameState.name !== BUDGET_GAME_STATES.budgeting ||
      // No budget category code
      !categoryCode ||
      // No options found for this category
      (currentCategoryOptions?.length ?? 0) === 0 ||
      // Already selected an option for this category
      !!budgetPlanOptionsByBudgetCategoryCode[categoryCode] ||
      // More than one option and it's not the emergency category
      ((currentCategoryOptions?.length ?? 0) > 1 &&
        categoryCode !== EMERGENCY_BUDGET_CODE) ||
      // Already auto-selected for this category in this session (prevents
      // double-POST race when effect re-fires before server state catches up)
      autoSelectedCategoriesRef.current.has(categoryCode)
    ) {
      return;
    }

    // Auto-select the only option by default
    let optionId = currentCategoryOptions[0].id;

    // Emergency category: auto-select random option
    if (categoryCode === EMERGENCY_BUDGET_CODE) {
      const randomIndex = Math.floor(
        Math.random() * currentCategoryOptions.length,
      );
      optionId = currentCategoryOptions[randomIndex].id;
    }

    autoSelectedCategoriesRef.current.add(categoryCode);

    dispatch({
      type: BUDGET_GAME_ACTIONS.SELECT_OPTION,
      payload: { optionId },
    });
  }, [
    gameState.name,
    currentSlideConfig?.budgetCategoryCode,
    currentCategoryOptions,
    budgetPlanOptionsByBudgetCategoryCode,
    isAddingBudgetPlanOption,
    isBudgetPlanFetching,
    budgetPlan?.id,
    dispatch,
  ]);

  // ===== DERIVED STATE =====

  const isLoading =
    gameState.name === BUDGET_GAME_STATES.initializing ||
    gameState.name === BUDGET_GAME_STATES.creatingPlan;

  // Instrumentation: surface stuck-ctx cases so Sentry catches lingering
  // state-machine desyncs after the self-loop fix.
  // TODO: Remove if no warnings, no sooner than May 2026.
  useEffect(() => {
    if (isLastSlide && gameState.name === BUDGET_GAME_STATES.complete) {
      const allOptionsSelected =
        (gameContext.budgetPlan?.options?.length ?? 0) >= slideConfigs.length;
      if (!allOptionsSelected) {
        console.warn("canAdvance blocked on last slide", {
          budgetPlanId: gameContext.budgetPlan?.id,
          optionsCount: gameContext.budgetPlan?.options?.length ?? 0,
          expected: slideConfigs.length,
          netIncome: gameContext.budgetPlan?.taxInfo?.netIncome ?? 0,
          costs: gameContext.budgetPlan?.budgetTotals?.costs ?? 0,
        });
      }
    }
  }, [
    isLastSlide,
    gameState.name,
    gameContext.budgetPlan,
    slideConfigs.length,
  ]);

  const isPlanCreationFailed =
    gameState.name === BUDGET_GAME_STATES.planCreationFailed;

  return {
    // State
    gameState,
    gameContext,
    budgetPlan,

    // Dispatch
    dispatch,

    // Derived values from state machine
    currentSlideConfig,
    budgetPlanOptionsByBudgetCategoryCode,
    canAdvance,
    canViewSummary,
    isFirstSlide,
    isLastSlide,

    // Error state
    hasError: !!errorMessage,
    errorMessage,
    clearError,
    isPlanCreationFailed,
    planCreationError: gameContext.planCreationError,

    // Loading/saving states
    isLoading,
    isSaving,
    isCreatingBudgetPlan,
    isDeletingBudgetPlan,
    isUpdatingBudgetPlan,
    isAddingBudgetPlanOption,
    isAddBudgetPlanOptionSuccess,
    isBudgetPlanFetching,
  };
};
